var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { MGPFallible, MGPOptional, Set, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player } from '../../jscaip/Player';
import { ConfigurableRules } from '../../jscaip/Rules';
import { Debug } from '../../utils/Debug';
import { GoFailure } from './GoFailure';
import { GoMove } from './GoMove';
import { GoPhase } from './GoPhase';
import { GoPiece } from './GoPiece';
export class GoNode extends GameNode {
}
let AbstractGoRules = class AbstractGoRules extends ConfigurableRules {
    playOnIntersection;
    constructor(playOnIntersection) {
        super();
        this.playOnIntersection = playOnIntersection;
    }
    getZoom(_config) {
        return 1;
    }
    getNewKo(move, newBoard, goLegalityInformation, config) {
        if (goLegalityInformation.uniqueCapture.isPresent()) {
            const captured = goLegalityInformation.uniqueCapture.get();
            const capturerCoord = move.coord;
            const capturer = newBoard[capturerCoord.y][capturerCoord.x];
            const maxZoom = this.getZoom(config);
            for (let zoom = 1; zoom <= maxZoom; zoom++) {
                const goGroupDataFactory = this.getGoGroupDataFactory(zoom);
                const capturersInfo = goGroupDataFactory.getGroupData(capturerCoord, newBoard);
                const capturersFreedoms = capturersInfo.emptyCoords;
                const capturersGroup = GoPiece.pieceBelongTo(capturer, Player.ZERO) ? capturersInfo.darkCoords : capturersInfo.lightCoords;
                if (capturersFreedoms.length === 1 &&
                    capturersFreedoms[0].equals(captured) &&
                    capturersGroup.length === 1) {
                    return MGPOptional.of(captured);
                }
            }
        }
        return MGPOptional.empty();
    }
    markTerritoryAndCount(state) {
        const resultingBoard = state.getCopiedBoard();
        const emptyZones = this.getTerritoryLikeGroup(state);
        const captured = state.getCapturedCopy();
        for (const emptyZone of emptyZones) {
            const pointMaker = emptyZone.getWrapper();
            if (pointMaker === GoPiece.LIGHT) {
                // light territory
                captured.add(Player.ONE, emptyZone.emptyCoords.length);
                for (const territory of emptyZone.getCoords()) {
                    resultingBoard[territory.y][territory.x] = GoPiece.LIGHT_TERRITORY;
                }
            }
            else {
                Utils.assert(pointMaker === GoPiece.DARK, 'territory should be wrapped by dark or light, not by ' + pointMaker.toString());
                // dark territory
                captured.add(Player.ZERO, emptyZone.emptyCoords.length);
                for (const territory of emptyZone.getCoords()) {
                    resultingBoard[territory.y][territory.x] = GoPiece.DARK_TERRITORY;
                }
            }
        }
        return state.withBoard(resultingBoard).withCaptures(captured);
    }
    removeAndSubtractTerritory(state) {
        const resultingBoard = state.getCopiedBoard();
        const captured = state.getCapturedCopy();
        for (const coordAndContent of state.getCoordsAndContents()) {
            const coord = coordAndContent.coord;
            if (coordAndContent.content.isTerritory()) {
                resultingBoard[coord.y][coord.x] = GoPiece.EMPTY;
                const owner = coordAndContent.content.getOwner();
                captured.add(owner, -1);
            }
        }
        return state.withBoard(resultingBoard).withCaptures(captured);
    }
    getEmptyZones(deadlessState) {
        return this.getGoGroupDataFactory(1)
            .getGroupsDataWhere(deadlessState.getCopiedBoard(), (piece) => piece.isEmpty());
    }
    switchLiveness(groupCoord, switchedState, zoom) {
        const switchedBoard = switchedState.getCopiedBoard();
        const switchedPiece = switchedBoard[groupCoord.y][groupCoord.x];
        Utils.assert(switchedPiece.isOccupied(), `Can't switch emptyness aliveness`);
        const goGroupDataFactory = this.getGoGroupDataFactory(zoom);
        const group = goGroupDataFactory.getGroupData(groupCoord, switchedBoard);
        const captured = switchedState.getCapturedCopy();
        switch (group.color) {
            case GoPiece.DEAD_DARK:
                captured.add(Player.ONE, -2 * group.deadDarkCoords.length);
                for (const deadDarkCoord of group.deadDarkCoords) {
                    switchedBoard[deadDarkCoord.y][deadDarkCoord.x] = GoPiece.DARK;
                }
                break;
            case GoPiece.DEAD_LIGHT:
                captured.add(Player.ZERO, -2 * group.deadLightCoords.length);
                for (const deadLightCoord of group.deadLightCoords) {
                    switchedBoard[deadLightCoord.y][deadLightCoord.x] = GoPiece.LIGHT;
                }
                break;
            case GoPiece.LIGHT:
                captured.add(Player.ZERO, 2 * group.lightCoords.length);
                for (const lightCoord of group.lightCoords) {
                    switchedBoard[lightCoord.y][lightCoord.x] = GoPiece.DEAD_LIGHT;
                }
                break;
            default:
                Utils.expectToBe(group.color, GoPiece.DARK);
                captured.add(Player.ONE, 2 * group.darkCoords.length);
                for (const darkCoord of group.darkCoords) {
                    switchedBoard[darkCoord.y][darkCoord.x] = GoPiece.DEAD_DARK;
                }
                break;
        }
        return switchedState.withBoard(switchedBoard).withCaptures(captured);
    }
    isPass(move) {
        return move.equals(GoMove.PASS);
    }
    isAccept(move) {
        return move.equals(GoMove.ACCEPT);
    }
    isLegalDeadMarking(move, state) {
        return state.getPieceAt(move.coord).isOccupied() &&
            (state.phase.isCounting() || state.phase.isAccept());
    }
    isLegalDrop(move, state, config) {
        if (this.isKo(move, state)) {
            return MGPFallible.failure(GoFailure.ILLEGAL_KO());
        }
        if (state.phase.isCounting() || state.phase.isAccept()) {
            state = this.resurrectStones(state);
        }
        const goLegalityInformation = this.applyCaptures(move, state, config);
        const postCaptureState = goLegalityInformation.postCaptureState;
        const droppedPieceHasFreedom = this.doesPieceHaveFreedoms(move.coord, postCaptureState.withPieceAt(move.coord, GoPiece.ofPlayer(postCaptureState.getCurrentPlayer())), config);
        if (droppedPieceHasFreedom) {
            return MGPFallible.success(goLegalityInformation);
        }
        else {
            return MGPFallible.failure(GoFailure.CANNOT_COMMIT_SUICIDE());
        }
    }
    doesPieceHaveFreedoms(coord, state, config) {
        const boardCopy = state.getCopiedBoard();
        boardCopy[coord.y][coord.x] = GoPiece.ofPlayer(state.getCurrentPlayer());
        const maxZoom = this.getZoom(config);
        for (let zoom = 1; zoom <= maxZoom; zoom++) {
            const goGroupDataFactory = this.getGoGroupDataFactory(zoom);
            const goGroupsData = goGroupDataFactory.getGroupData(coord, boardCopy);
            const isSuicide = goGroupsData.emptyCoords.length === 0;
            if (isSuicide) {
                return false;
            }
        }
        return true;
    }
    isKo(move, state) {
        if (state.koCoord.isPresent()) {
            return move.coord.equals(state.koCoord.get());
        }
        else {
            return false;
        }
    }
    /**
     * will remove captured pieces
     * will add captured pieces count to captures
     * will return an optional coord if there is a coord that is the only capture
     * (if there is strictly one capture, optional will be present, else it'll be absent)
     */
    applyCaptures(move, state, config) {
        const captureds = [];
        const maxZoom = this.getZoom(config);
        for (let zoom = 1; zoom <= maxZoom; zoom++) {
            const goGroupDataFactory = this.getGoGroupDataFactory(zoom);
            for (const direction of goGroupDataFactory.getDirections(move.coord)) {
                const captures = this.getCapturedInDirection(move.coord, direction, state, goGroupDataFactory);
                captureds.push(...captures);
            }
        }
        const capturedSet = new Set(captureds);
        let resultingState = state;
        for (const captured of capturedSet) {
            resultingState = resultingState.withPieceAt(captured, GoPiece.EMPTY);
        }
        resultingState = resultingState.withAddedCaptures(resultingState.getCurrentPlayer(), capturedSet.size());
        return {
            postCaptureState: resultingState,
            uniqueCapture: capturedSet.size() === 1 ?
                MGPOptional.of(capturedSet.getAnyElement().get()) :
                MGPOptional.empty(),
        };
    }
    getCapturedInDirection(coord, vector, state, goGroupDataFactory) {
        const neightbooringCoord = coord.getNext(vector);
        const copiedBoard = state.getCopiedBoard();
        if (this.isReachable(neightbooringCoord, state)) {
            const opponent = GoPiece.ofPlayer(state.getCurrentOpponent());
            if (state.getPieceAt(neightbooringCoord) === opponent) {
                Debug.display('GoRules', 'getCapturedInDirection', 'a group could be captured');
                const neightbooringGroup = goGroupDataFactory.getGroupData(neightbooringCoord, copiedBoard);
                const koCoord = state.koCoord;
                if (this.isCapturableGroup(neightbooringGroup, koCoord)) {
                    Debug.display('GoRules', 'getCapturedInDirection', {
                        neightbooringGroupCoord: neightbooringGroup.getCoords(),
                        message: 'is capturable',
                    });
                    return neightbooringGroup.getCoords();
                }
            }
        }
        return [];
    }
    isReachable(coord, state) {
        return state.hasInequalPieceAt(coord, GoPiece.UNREACHABLE);
    }
    isCapturableGroup(groupData, koCoord) {
        if (groupData.color.isOccupied() && groupData.emptyCoords.length === 1) {
            return koCoord.equalsValue(groupData.emptyCoords[0]) === false; // Ko Rules Block Capture
        }
        else {
            return false;
        }
    }
    getTerritoryLikeGroup(state) {
        const emptyGroups = this.getEmptyZones(state);
        return emptyGroups.filter((currentGroup) => currentGroup.isMonoWrapped());
    }
    applyPass(state) {
        const originalPhase = state.phase;
        Utils.assert(originalPhase.isPlaying() || originalPhase.isPassed(), 'Cannot pass in counting phase!');
        const newPhase = originalPhase.isPassed() ? GoPhase.COUNTING : GoPhase.PASSED;
        let passedState = state
            .incrementTurn()
            .withKo(MGPOptional.empty())
            .withPhase(newPhase);
        if (originalPhase.isPassed()) {
            passedState = this.markTerritoryAndCount(passedState);
        }
        return passedState;
    }
    applyLegalAccept(state) {
        const phase = state.phase.isCounting() ? GoPhase.ACCEPT : GoPhase.FINISHED;
        return state
            .incrementTurn()
            .withKo(MGPOptional.empty())
            .withPhase(phase);
    }
    applyNormalLegalMove(legalMove, goLegalityInformation, config) {
        let state = goLegalityInformation.postCaptureState;
        if (state.phase.isCounting() || state.phase.isAccept()) {
            state = this.resurrectStones(state);
        }
        const currentPlayer = state.getCurrentPlayer();
        const currentPlayerPiece = GoPiece.ofPlayer(currentPlayer);
        const postDropState = state.withPieceAt(legalMove.coord, currentPlayerPiece);
        const newKoCoord = this.getNewKo(legalMove, postDropState.getCopiedBoard(), goLegalityInformation, config);
        const newCaptured = state.captured;
        return postDropState
            .withCaptures(newCaptured)
            .incrementTurn()
            .withKo(newKoCoord)
            .withPhase(GoPhase.PLAYING);
    }
    resurrectStones(state) {
        for (let y = 0; y < state.getHeight(); y++) {
            for (let x = 0; x < state.getWidth(); x++) {
                if (state.getPieceAtXY(x, y).isDead()) {
                    state = this.switchLiveness(new Coord(x, y), state, 1);
                }
            }
        }
        return this.removeAndSubtractTerritory(state);
    }
    applyDeadMarkingMove(legalMove, state) {
        const territorylessState = this.removeAndSubtractTerritory(state);
        const switchedState = this.switchLiveness(legalMove.coord, territorylessState, 1);
        const resultingState = switchedState
            .incrementTurn()
            .withKo(MGPOptional.empty())
            .withPhase(GoPhase.COUNTING);
        return this.markTerritoryAndCount(resultingState);
    }
    isLegal(move, state, config) {
        const defaultSuccess = MGPFallible.success({
            postCaptureState: state,
            uniqueCapture: MGPOptional.empty(),
        });
        if (this.isPass(move)) {
            const playing = state.phase.isPlaying();
            const passed = state.phase.isPassed();
            Debug.display('GoRules', 'isLegal', 'at ' + state.phase + ((playing || passed) ? ' forbid' : ' allowed') +
                ' passing on ' + state.getCopiedBoard());
            if (playing || passed) {
                return defaultSuccess;
            }
            else {
                return MGPFallible.failure(GoFailure.CANNOT_PASS_AFTER_PASSED_PHASE());
            }
        }
        else if (this.isAccept(move)) {
            const counting = state.phase.isCounting();
            const accept = state.phase.isAccept();
            if (counting || accept) {
                return defaultSuccess;
            }
            else {
                return MGPFallible.failure(GoFailure.CANNOT_ACCEPT_BEFORE_COUNTING_PHASE());
            }
        }
        else if (state.getPieceAt(move.coord).isOccupied()) {
            Debug.display('GoRules', 'isLegal', 'move is marking');
            const legal = this.isLegalDeadMarking(move, state);
            if (legal) {
                return defaultSuccess;
            }
            else {
                if (this.playOnIntersection) {
                    return MGPFallible.failure(GoFailure.OCCUPIED_INTERSECTION());
                }
                else {
                    return MGPFallible.failure(GoFailure.OCCUPIED_SPACE());
                }
            }
        }
        else {
            Debug.display('GoRules', 'isLegal', 'move is normal stuff: ' + move.toString());
            return this.isLegalDrop(move, state, config);
        }
    }
    applyLegalMove(legalMove, state, config, infos) {
        if (this.isPass(legalMove)) {
            return this.applyPass(state);
        }
        else if (this.isAccept(legalMove)) {
            return this.applyLegalAccept(state);
        }
        else if (this.isLegalDeadMarking(legalMove, state)) {
            return this.applyDeadMarkingMove(legalMove, state);
        }
        else {
            return this.applyNormalLegalMove(legalMove, infos, config);
        }
    }
    getGameStatus(node) {
        const state = node.gameState;
        if (state.phase.isFinished()) {
            const captured = state.captured;
            const capturedZero = captured.get(Player.ZERO);
            const capturedOne = captured.get(Player.ONE);
            if (capturedOne < capturedZero) {
                return GameStatus.ZERO_WON;
            }
            else if (capturedZero < capturedOne) {
                return GameStatus.ONE_WON;
            }
            else {
                return GameStatus.DRAW;
            }
        }
        else {
            return GameStatus.ONGOING;
        }
    }
};
AbstractGoRules = __decorate([
    Debug.log
], AbstractGoRules);
export { AbstractGoRules };
